import { type ListingApplicationPayload } from 'api/ats';
import type { ApiListingApplicationListingType } from 'api/types/ApiListingApplicationListingType';
import type {
  ApiEventId,
  ApiInternshipId,
  ApiJobId,
  ApiListingApplicationId,
  ApiOrgId,
  ApiVolopId,
} from 'api/types/ApiTypedId';
import type { FormSubmitFunction } from 'components/Form/FormContext';
import { useEnvironment, useUser } from 'contexts/UserEnvironmentContext';
import { useI18n } from 'i18n/hooks/useI18n';
import { ApiOrgType } from 'modules/listing/orgs/api/types/ApiOrgType';

import { useListingApplicationSubmissionAsGuest } from './useListingApplicationSubmissionAsGuest';
import { useListingApplicationSubmissionAsUser } from './useListingApplicationSubmissionAsUser';

type Args = {
  listingId: ApiJobId | ApiInternshipId | ApiVolopId | ApiEventId;
  listingType: ApiListingApplicationListingType;
  orgId: ApiOrgId | null;
  orgType: ApiOrgType | null;
  onApplied: ((listingApplicationId: ApiListingApplicationId) => void) | null;
};

export function useListingApplicationSubmission({
  listingId,
  listingType,
  orgId,
  orgType,
  onApplied,
}: Args) {
  const { locale } = useI18n();
  const { user } = useUser();
  const { environment } = useEnvironment();

  const { getInitialUserValues, submitListingApplicationAsUser } =
    useListingApplicationSubmissionAsUser({
      listingId,
      listingType,
      orgId,
      orgType,
      locale,
      onApplied,
    });

  const { submitListingApplicationAsGuest } =
    useListingApplicationSubmissionAsGuest({
      listingType,
    });

  const submitListingApplication: FormSubmitFunction<
    ListingApplicationPayload
  > = async (formSubmitData) => {
    const { handleError } = formSubmitData;

    try {
      if (user && environment) {
        await submitListingApplicationAsUser({
          user,
          environment,
          formSubmitData,
        });
      } else {
        await submitListingApplicationAsGuest(formSubmitData);
      }
    } catch (error) {
      handleError(error as Error);
    }
  };

  return {
    submitListingApplication,
    listingApplicationInitialValues:
      user && environment ? getInitialUserValues({ user, environment }) : {},
  };
}
